// Default league slug (used as fallback for nav). Real league data now comes from DB.
export const DEFAULT_LEAGUE_SLUG = "fifa-2026";
export const DEMO_LEAGUE_SLUG = "demo-2026";

// Kept for backwards-compat with existing components that haven't been refactored yet.
export const CURRENT_LEAGUE_SLUG = DEFAULT_LEAGUE_SLUG;
export const CURRENT_LEAGUE_NAME = "FIFA 2026 World Cup";

export function bracketSlug(bracketName: string): string {
  return bracketName
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Code format rules (kept in sync with validateLeagueCode + server zod schema):
// - One or more PREFIX segments separated by "-": each 2–12 chars, A–Z or 0–9
// - Final SUFFIX segment: exactly 5 chars from a 30-char alphabet (no I/O/0/1),
//   must contain at least 2 letters AND at least 2 digits
// Example: FAMILY-X7K2A, FAMILY-LEAGUE-9A4BC
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no I, O, 0, 1
const CODE_LETTERS = /[A-HJ-NP-Z]/g;
const CODE_DIGITS = /[2-9]/g;
const SUFFIX_LEN = 5;
export const LEAGUE_CODE_MAX_LENGTH = 32;

export const LEAGUE_CODE_FORMAT_HINT =
  "Must end with a dash and 5 characters mixing letters and numbers (e.g. FAMILY-X7K2A). Max 32 characters.";

function randomSuffix(): string {
  while (true) {
    let s = "";
    for (let i = 0; i < SUFFIX_LEN; i++) {
      s += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
    }
    const letters = (s.match(CODE_LETTERS) || []).length;
    const digits = (s.match(CODE_DIGITS) || []).length;
    if (letters >= 2 && digits >= 2) return s;
  }
}

// Generate a readable league join code: PREFIX-XXXXX (e.g., FAMILY-X7K2A)
export function generateLeagueCode(prefix?: string): string {
  const cleanPrefix = (prefix || "LEAGUE")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "")
    .slice(0, 12) || "LEAGUE";
  return `${cleanPrefix}-${randomSuffix()}`;
}

/** Validate a manually entered league code. Case-insensitive (caller should uppercase). */
export function validateLeagueCode(raw: string): { ok: true } | { ok: false; error: string } {
  const code = raw.trim().toUpperCase();
  if (!code) return { ok: false, error: "Code is required." };
  if (code.length > LEAGUE_CODE_MAX_LENGTH) {
    return { ok: false, error: `Code must be ${LEAGUE_CODE_MAX_LENGTH} characters or fewer.` };
  }
  const segments = code.split("-");
  if (segments.length < 2) {
    return { ok: false, error: "Code must contain a '-' separating the prefix from the suffix." };
  }
  const suffix = segments[segments.length - 1];
  const prefixSegments = segments.slice(0, -1);

  for (const seg of prefixSegments) {
    if (!/^[A-Z0-9]{2,12}$/.test(seg)) {
      return {
        ok: false,
        error: "Each prefix segment must be 2–12 characters, letters A–Z and digits only.",
      };
    }
  }
  if (suffix.length !== SUFFIX_LEN) {
    return { ok: false, error: `Suffix must be exactly ${SUFFIX_LEN} characters.` };
  }
  if (!/^[A-HJ-NP-Z2-9]+$/.test(suffix)) {
    return {
      ok: false,
      error: "Suffix may only use letters A–Z (no I/O) and digits 2–9 (no 0/1).",
    };
  }
  const letters = (suffix.match(CODE_LETTERS) || []).length;
  const digits = (suffix.match(CODE_DIGITS) || []).length;
  if (letters < 2 || digits < 2) {
    return {
      ok: false,
      error: "Suffix must mix at least 2 letters and 2 digits.",
    };
  }
  // Reject all-same suffix (e.g. AAAA2 — already blocked by mix rule, but be explicit)
  if (new Set(suffix.split("")).size < 3) {
    return { ok: false, error: "Suffix is too repetitive — try generating a new one." };
  }
  return { ok: true };
}
